import TenancyAgreementCard from "../../components/TenancyAgreementCard";
import { canRenewRent, renewalUnavailableMessage } from "../../utils/canRenewRent";
import useTenantVerificationMissing from "../../hooks/useTenantVerificationMissing";
import TenantVerificationPrompt from "../../components/TenantVerificationPrompt";
import { useState, useEffect, useCallback, useRef } from "react";
import { Link, useNavigate, useParams, useLocation } from "react-router-dom";
import Modal from "react-responsive-modal";
import { DashboardLayout } from "../../components/layouts/DashboardLayout";
import styles from "./BookingDetail.module.css";
import {
  getTransactionByReference,
  getSinglePropertyTransaction,
  resumePayment,
  cancelTransaction,
  connectToTransactionEvents,
  getBookingWaitStatus,
  TransactionEventPayload,
} from "@services/krent.service";
import { toast } from "react-toastify";
import RatingsAndReviews from "../../components/shared/reviews/review-card";
import { useSessionStorage } from "../../hooks";
import { formatStatusLabel, rentStatusMessages } from "../../helpers/statusStyles";
import {
  ClockIcon,
  isWaitingForHost,
  useWaitWindowCountdown,
  TERMINAL_WAIT_STATUSES,
} from "../../components/shared/cards/booking-timer";
import PaymentMethodModal from "../../components/PaymentMethodModal";
import CautionFeeRefundButton from "../../components/CautionFeeRefundButton";

interface PropertyDetails {
  name: string;
  rating: number;
  reviewCount: number;
  location: string;
  ref: string;
  status: string;
  isInstant: boolean;
  guests: number;
  bedrooms: number;
  bathrooms: number;
  squareFoot: number;
  imageUrl: string;
}

interface StayDetails {
  checkIn: string;
  checkOut: string;
  guests: number;
  nightlyRate: number;
  nights: number;
  serviceFee: number;
  cautionFee: number;
}

interface GuestInfo {
  name: string;
  initials: string;
  email: string;
  phone: string;
  whatsapp: string;
  specialRequests: string;
}

interface HostInfo {
  name: string;
  memberSince: string;
  isVerified: boolean;
  email: string;
  phone: string;
  imageUrl: string;
}

const formatNaira = (amount: number): string =>
  `₦${amount.toLocaleString("en-NG")}`;

export default function BookingDetail() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const verificationMissing = useTenantVerificationMissing();
  const [user] = useSessionStorage("user", {});
  const [transaction, setTransaction] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [resumingPayment, setResumingPayment] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showPaystackSuccess, setShowPaystackSuccess] = useState(false);
  const hostAcceptedToastShown = useRef(false);
  const [specialRequests, setSpecialRequests] = useState<string>("");
  const [propertyImageFailed, setPropertyImageFailed] = useState(false);
  const [hostImageFailed, setHostImageFailed] = useState(false);

  useEffect(() => {
    if (!id) return;

    // If the id looks like a MongoDB ObjectId (24 hex chars), fetch by id directly
    const isObjectId = /^[a-f0-9]{24}$/i.test(id);

    const fetchPromise = isObjectId
      ? getSinglePropertyTransaction(id, new AbortController().signal)
      : getTransactionByReference(id);

    fetchPromise
      .then((res: any) => {
        const transactionData = res?.data || res;
        setTransaction(transactionData);
        setPropertyImageFailed(false);
        setHostImageFailed(false);
        if (
          transactionData?.specialRequests !== undefined &&
          transactionData?.specialRequests !== null
        ) {
          setSpecialRequests(transactionData.specialRequests);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  }, [id]);

  // Detect Paystack redirect-back (?trxref or ?reference on this page)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const trxref = params.get("trxref") || params.get("reference");
    if (trxref) {
      setShowPaystackSuccess(true);
      // Clean the URL without triggering a re-fetch
      navigate(location.pathname, { replace: true });
    }
  }, [location.search, location.pathname, navigate]);

  const handleHostWaitStatusUpdate = useCallback(
    (
      nextStatus: string,
      nextExpiresAt?: string | null,
      nextHostAccepted?: boolean,
    ) => {
      setTransaction((prev: any) =>
        prev
          ? {
              ...prev,
              status: nextStatus,
              ...(nextExpiresAt ? { sessionExpiresAt: nextExpiresAt } : {}),
              ...(nextHostAccepted != null
                ? { hostAcceptedBankTransfer: nextHostAccepted }
                : {}),
            }
          : prev,
      );

      if (nextStatus === "confirmed") {
        toast.success("Your host accepted this booking.");
        return;
      }

      if (nextStatus === "failed_unavailable") {
        toast.error("Your host declined this booking.");
        return;
      }

      if (nextStatus === "timeout_unconfirmed") {
        toast.info("The host did not respond in time.");
        return;
      }

      if (
        nextHostAccepted &&
        nextStatus === "paid_pending_confirmation" &&
        !hostAcceptedToastShown.current
      ) {
        hostAcceptedToastShown.current = true;
        toast.success("Your host accepted. We're confirming your payment.");
      }
    },
    [],
  );

  const isWaitingForHostResponse =
    !!transaction &&
    (transaction?.bookingFlow === "request" ||
      !transaction?.listing?.instantBooking) &&
    !!transaction?.sessionExpiresAt &&
    isWaitingForHost(transaction?.status);

  const isReceiptUnderReview =
    transaction?.paymentMethod === "manual_bank_transfer" &&
    transaction?.status === "paid_pending_confirmation";

  const showsHostWaitCountdown =
    isWaitingForHostResponse && !isReceiptUnderReview;

  const {
    expired: hostWaitExpired,
    formattedTime: hostWaitTime,
    timeLeft: hostWaitTimeLeft,
  } = useWaitWindowCountdown(
    showsHostWaitCountdown ? transaction?.sessionExpiresAt : undefined,
  );

  // Hide the timer as soon as the wait window expires or host responds
  const showHostWaitTimer =
    showsHostWaitCountdown && !hostWaitExpired && hostWaitTimeLeft > 0;

  useEffect(() => {
    if (!transaction?.id || !isWaitingForHostResponse) {
      return;
    }

    let cancelled = false;
    let eventSource: EventSource | null = null;
    let pollIntervalId: ReturnType<typeof setInterval> | null = null;
    let reconnectTimeoutId: ReturnType<typeof setTimeout> | null = null;
    let reconnectAttempt = 0;

    const cleanupRealtime = () => {
      if (eventSource) {
        eventSource.close();
        eventSource = null;
      }
      if (pollIntervalId) {
        clearInterval(pollIntervalId);
        pollIntervalId = null;
      }
      if (reconnectTimeoutId) {
        clearTimeout(reconnectTimeoutId);
        reconnectTimeoutId = null;
      }
    };

    const applyPayload = (payload: TransactionEventPayload) => {
      if (cancelled || !payload.status) return;
      handleHostWaitStatusUpdate(
        payload.status,
        payload.sessionExpiresAt,
        payload.hostAcceptedBankTransfer,
      );
      if (TERMINAL_WAIT_STATUSES.includes(payload.status)) {
        cleanupRealtime();
      }
    };

    const pollStatus = async () => {
      try {
        const data = await getBookingWaitStatus(transaction.id);
        if (!cancelled && data?.status) {
          applyPayload(data);
        }
      } catch {
        // polling is a fallback; ignore transient errors
      }
    };

    const connectSse = () => {
      if (cancelled) return;

      eventSource = connectToTransactionEvents(
        transaction.id,
        applyPayload,
        () => {
          if (cancelled) return;
          eventSource?.close();
          eventSource = null;
          reconnectAttempt += 1;
          const delay = Math.min(1000 * 2 ** reconnectAttempt, 30000);
          reconnectTimeoutId = setTimeout(connectSse, delay);
        },
      );
    };

    connectSse();
    pollIntervalId = setInterval(pollStatus, 8000);
    pollStatus();

    return () => {
      cancelled = true;
      cleanupRealtime();
    };
  }, [transaction?.id, isWaitingForHostResponse, handleHostWaitStatusUpdate]);

  useEffect(() => {
    if (!hostWaitExpired || !showsHostWaitCountdown || !transaction?.id) {
      return;
    }

    let cancelled = false;

    const verifyTimeout = async () => {
      try {
        const data = await getBookingWaitStatus(transaction.id);
        if (cancelled || !data?.status) return;

        if (data.status === "timeout_unconfirmed") {
          handleHostWaitStatusUpdate(
            data.status,
            data.sessionExpiresAt,
            data.hostAcceptedBankTransfer,
          );
          return;
        }

        if (isWaitingForHost(data.status)) {
          if (data.hostAcceptedBankTransfer) {
            handleHostWaitStatusUpdate(
              data.status,
              data.sessionExpiresAt,
              data.hostAcceptedBankTransfer,
            );
          }
          return;
        }

        handleHostWaitStatusUpdate(
          data.status,
          data.sessionExpiresAt,
          data.hostAcceptedBankTransfer,
        );
      } catch {
        handleHostWaitStatusUpdate("timeout_unconfirmed");
      }
    };

    verifyTimeout();
    const intervalId = setInterval(verifyTimeout, 5000);

    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [
    hostWaitExpired,
    showsHostWaitCountdown,
    transaction?.id,
    handleHostWaitStatusUpdate,
  ]);

  const handleResumePayment = async () => {
    if (!transaction?.id) return;

    setResumingPayment(true);
    try {
      const response: any = await resumePayment(transaction.id);

      if (response?.errors && response.errors.length > 0) {
        toast.error(response.errors[0].message || "Failed to resume payment");
        return;
      }

      const paymentUrl =
        response?.data?.authorization_url || response?.authorization_url;
      if (paymentUrl) {
        window.location.href = paymentUrl;
      }
    } catch (error: any) {
      toast.error(error?.message || "Failed to resume payment");
    } finally {
      setResumingPayment(false);
    }
  };

  const handleCancelTransaction = async () => {
    if (!transaction?.id) return;

    setCancelling(true);
    try {
      const response: any = await cancelTransaction(
        transaction.id,
        "Cancelled before payment",
      );

      if (response?.errors && response.errors.length > 0) {
        toast.error(
          response.errors[0].message || "Failed to cancel transaction",
        );
        return;
      }

      toast.success("Transaction cancelled successfully");
      setShowCancelModal(false);
      setTransaction(response?.data || { ...transaction, status: "cancelled" });
    } catch (error: any) {
      toast.error(
        error?.errors?.[0]?.message ||
          error?.message ||
          "Failed to cancel transaction",
      );
    } finally {
      setCancelling(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="text-center py-5">Loading...</div>
      </DashboardLayout>
    );
  }

  const listing = transaction?.listing;
  const propertySlug = listing?.slug || listing?.id || listing?._id || "";
  const propertyData: PropertyDetails = {
    name: listing?.title || "",
    rating: listing?.rating || 0,
    reviewCount: listing?.reviewCount || 0,
    location: listing?.location?.city || "",
    ref: propertySlug,
    status: transaction?.status || "",
    isInstant: listing?.instantBooking || false,
    guests: listing?.guests || 0,
    bedrooms: listing?.bedrooms || 0,
    bathrooms: listing?.bathrooms || 0,
    squareFoot: listing?.squareFoot || 0,
    imageUrl: listing?.coverImage || "",
  };

  const bill = Array.isArray(transaction?.bill) ? transaction.bill[transaction.bill.length - 1] || {} : transaction?.bill || {};

  // Calculate nights from dates
  const calculateNights = (arrival: string, departure: string): number => {
    if (!arrival || !departure) return 0;
    const arrDate = new Date(arrival);
    const depDate = new Date(departure);
    const diffTime = depDate.getTime() - arrDate.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? diffDays : 0;
  };

  const nights =
    transaction?.arrival_date && transaction?.departure_date
      ? calculateNights(transaction.arrival_date, transaction.departure_date)
      : 0;

  const stayData: StayDetails = {
    checkIn: transaction?.arrival_date
      ? new Date(transaction.arrival_date).toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
        })
      : "",
    checkOut: transaction?.departure_date
      ? new Date(transaction.departure_date).toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
        })
      : "",
    guests: transaction?.guest?.adult || 0,
    nightlyRate: nights > 0 ? (bill?.propertyPrice || 0) / nights : 0,
    nights: nights,
    serviceFee: bill?.serviceCharge || 0,
    cautionFee: bill?.cautionFee || 0,
  };

  const guestData: GuestInfo = {
    name: `${transaction?.renter?.firstname || ""} ${transaction?.renter?.lastname || ""}`.trim(),
    initials:
      `${transaction?.renter?.firstname?.[0] || ""}${transaction?.renter?.lastname?.[0] || ""}`.toUpperCase(),
    email: transaction?.renter?.email || "",
    phone: transaction?.renter?.phone || "",
    whatsapp: transaction?.renter?.whatsappNumber || "",
    specialRequests: specialRequests || "",
  };

  const isOwnGuest =
    String(transaction?.renter?.id || "") === String(user?.id || "");

  const hostData: HostInfo = {
    name: `${transaction?.owner?.firstname || ""} ${transaction?.owner?.lastname || ""}`,
    memberSince: "Member since 2024",
    isVerified: transaction?.owner?.kycVerified || false,
    email: transaction?.owner?.email || "",
    phone: transaction?.owner?.phone || "",
    imageUrl: transaction?.owner?.photoUrl || "",
  };

  const totalPaid =
    bill?.total ||
    stayData.nightlyRate * stayData.nights +
      stayData.serviceFee +
      stayData.cautionFee;

  const handleDownloadReceipt = () => {
    navigate(`/my-bookings/${encodeURIComponent(id || transaction?.id || transaction?._id)}/receipt`);
  };

  const isLongLease = transaction?.productType === "rent" || listing?.intent === "long lease";

  const isPaymentPendingConfirmation = transaction?.status !== "confirmed";

  const showCautionFeeRefund =
    ["confirmed", "completed"].includes(transaction?.status) &&
    Number(bill?.cautionFee) > 0;

  const rentIsMonthly = transaction?.rentCycle === "monthly";
  const rentDuration = (rentIsMonthly ? transaction?.durationMonths : transaction?.durationYears) || 0;
  const rentDurationLabel = rentDuration ? `${rentDuration} ${rentIsMonthly ? "month" : "year"}${rentDuration === 1 ? "" : "s"}` : "";

  // ── Payment status screen (long-lease) ───────────────────────
  if (showPaystackSuccess && isLongLease) {
    const fmtDate = (d: string) =>
      d
        ? new Date(d).toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
          })
        : "";

    const rentStartDate = transaction?.commencementDate || transaction?.arrival_date || "";
    const rentEndDate = transaction?.departure_date || "";
    const rentDateLabel =
      rentStartDate && rentEndDate
        ? `${fmtDate(rentStartDate)} - ${fmtDate(rentEndDate)}`
        : "";


    const landlordFullName =
      `${transaction?.owner?.firstname || ""} ${transaction?.owner?.lastname || ""}`.trim();

    const periodRent = (rentIsMonthly ? bill?.rentPerMonth : bill?.rentPerYear) || bill?.propertyPrice || 0;
    const displayCautionFee = bill?.cautionFee || 0;
    const displayAdministrativeFee = bill?.administrativeFee ?? (bill?.agencyFee || 0) + (bill?.legalFee || 0);
    const displayServiceFee = bill?.serviceCharge || 0;

    return (
      <DashboardLayout
        title="Rent Property"
        subtitle="Select and rent property on krent"
      >
        <div>{transaction?.status === "confirmed" && <TenancyAgreementCard booking={transaction} />}</div>
        <TenantVerificationPrompt
          transactionId={transaction?.id || transaction?._id || ""}
        />
        <div
          style={{ maxWidth: "680px", margin: "0 auto", padding: "32px 16px" }}
        >
          {/* Check icon */}
          <div className="text-center mb-4">
            <div
              className="mx-auto d-flex align-items-center justify-content-center rounded-circle"
              style={{
                width: "72px",
                height: "72px",
                background: "#e6f4f1",
                border: "3px solid #47BBA9",
              }}
            >
              <svg
                width="34"
                height="34"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#47BBA9"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                {isPaymentPendingConfirmation ? (
                  <>
                    <circle cx="12" cy="12" r="9" />
                    <path d="M12 7v5l3 2" />
                  </>
                ) : (
                  <path d="M20 6L9 17l-5-5" />
                )}
              </svg>
            </div>
          </div>

          <h4
            className="fw-bold font-onest text-center mb-2"
            style={{ fontSize: "24px", color: "#1a1a1a" }}
          >
            {isPaymentPendingConfirmation
              ? formatStatusLabel(transaction?.status)
              : "Payment Confirmed"}
          </h4>
          <p
            className="text-center font-onest mb-1"
            style={{ color: "#6b7280", fontSize: "14px" }}
          >
            {isPaymentPendingConfirmation
              ? rentStatusMessages[transaction?.status] || "Your payment is being processed."
              : rentStatusMessages[transaction?.status] || "We've received your payment. Your rent payment is verified."}
          </p>
          {transaction?.referenceId && (
            <p
              className="text-center font-onest mb-5"
              style={{ fontSize: "13px", color: "#9ca3af" }}
            >
              Rent reference:{" "}
              <span style={{ color: "#1a1a1a", fontWeight: 600 }}>
                {transaction.referenceId}
              </span>
            </p>
          )}

          {/* Property card */}
          <div
            className="rounded-4 p-4 mb-4"
            style={{ border: "1px solid #e5e7eb", background: "#fff" }}
          >
            {/* Property info row */}
            <div className="d-flex gap-3 align-items-start mb-4">
              {listing?.coverImage && !propertyImageFailed ? (
                <img
                  src={listing.coverImage}
                  alt={listing?.title}
                  className="rounded-3 flex-shrink-0"
                  style={{ width: "100px", height: "80px", objectFit: "cover" }}
                  onError={() => setPropertyImageFailed(true)}
                />
              ) : (
                <div
                  className="rounded-3 flex-shrink-0 d-flex align-items-center justify-content-center"
                  style={{
                    width: "100px",
                    height: "80px",
                    background: "#f3f4f6",
                    color: "#9ca3af",
                    fontSize: "24px",
                  }}
                >
                  <i className="fa-regular fa-image" />
                </div>
              )}
              <div>
                <p
                  className="fw-bold font-onest mb-1"
                  style={{ fontSize: "16px", color: "#1a1a1a" }}
                >
                  {listing?.title || ""}
                </p>
                {(listing?.location?.city || listing?.location?.state) && (
                  <p
                    className="font-onest mb-0 d-flex align-items-center gap-1"
                    style={{ fontSize: "13px", color: "#6b7280" }}
                  >
                    <i
                      className="fa-solid fa-location-dot"
                      style={{ fontSize: "11px" }}
                    />
                    {[listing?.location?.city, listing?.location?.state]
                      .filter(Boolean)
                      .join(", ")}
                  </p>
                )}
              </div>
            </div>

            {/* Rent details */}
            <div
              className="d-flex flex-column gap-3 pb-4"
              style={{ borderBottom: "1px solid #f0f0f5" }}
            >
              {rentDuration > 0 && (
                <div
                  className="d-flex align-items-center gap-2 font-onest"
                  style={{ fontSize: "14px", color: "#374151" }}
                >
                  <i
                    className="fa-regular fa-clock"
                    style={{ color: "#9ca3af", width: "16px" }}
                  />
                  <span className="text-muted">Rent Duration</span>
                  <span className="ms-auto fw-semibold">
                    <button
                      type="button"
                      className="btn btn-sm border px-2 py-0"
                      style={{ fontSize: "12px" }}
                    >
                      -
                    </button>
                    &nbsp;{rentDurationLabel}&nbsp;
                    <button
                      type="button"
                      className="btn btn-sm border px-2 py-0"
                      style={{ fontSize: "12px" }}
                    >
                      +
                    </button>
                    &nbsp;Years of stay
                  </span>
                </div>
              )}
              {rentDateLabel && (
                <div
                  className="d-flex align-items-center gap-2 font-onest"
                  style={{ fontSize: "14px", color: "#374151" }}
                >
                  <i
                    className="fa-regular fa-calendar"
                    style={{ color: "#9ca3af", width: "16px" }}
                  />
                  <span className="text-muted">Rent Date</span>
                  <span className="ms-auto fw-semibold">{rentDateLabel}</span>
                </div>
              )}
              {landlordFullName && (
                <div
                  className="d-flex align-items-center gap-2 font-onest"
                  style={{ fontSize: "14px", color: "#374151" }}
                >
                  <i
                    className="fa-regular fa-user"
                    style={{ color: "#9ca3af", width: "16px" }}
                  />
                  <span className="text-muted">Landlord</span>
                  <span className="ms-auto fw-semibold">
                    {landlordFullName}
                  </span>
                </div>
              )}
            </div>

            {/* Payment summary */}
            <div className="pt-4">
              <p
                className="fw-bold font-onest mb-3"
                style={{ fontSize: "14px", color: "#1a1a1a" }}
              >
                Payment summary
              </p>
              {periodRent > 0 && (
                <div
                  className="d-flex justify-content-between font-onest mb-2"
                  style={{ fontSize: "14px", color: "#374151" }}
                >
                  <span>{rentIsMonthly ? "Monthly rent" : "Annual rent"}</span>
                  <span>₦{Number(periodRent).toLocaleString("en-NG")}</span>
                </div>
              )}
              {displayCautionFee > 0 && (
                <div
                  className="d-flex justify-content-between font-onest mb-2"
                  style={{ fontSize: "14px", color: "#374151" }}
                >
                  <span>Caution fee</span>
                  <span>
                    ₦{Number(displayCautionFee).toLocaleString("en-NG")}
                  </span>
                </div>
              )}
              {displayAdministrativeFee > 0 && (
                <div
                  className="d-flex justify-content-between font-onest mb-2"
                  style={{ fontSize: "14px", color: "#374151" }}
                >
                  <span>Administrative fee</span>
                  <span>
                    ₦{Number(displayAdministrativeFee).toLocaleString("en-NG")}
                  </span>
                </div>
              )}
              {displayServiceFee > 0 && (
                <div
                  className="d-flex justify-content-between font-onest mb-2"
                  style={{ fontSize: "14px", color: "#374151" }}
                >
                  <span>Service fee</span>
                  <span>
                    ₦{Number(displayServiceFee).toLocaleString("en-NG")}
                  </span>
                </div>
              )}
              <div
                className="d-flex justify-content-between font-onest mt-3 pt-3"
                style={{
                  borderTop: "1px solid #f0f0f5",
                  fontSize: "16px",
                  fontWeight: 700,
                  color: "#1a1a1a",
                }}
              >
                <span>Total paid</span>
                <span>₦{Number(totalPaid).toLocaleString("en-NG")}</span>
              </div>
            </div>

            {/* Action buttons */}
            <div className="d-flex gap-2 mt-4 flex-wrap">
              <button
                type="button"
                className="btn flex-fill font-onest fw-semibold rounded-3"
                style={{
                  background: "#f3f4f6",
                  color: "#374151",
                  border: "1px solid #e5e7eb",
                  fontSize: "14px",
                  padding: "10px 16px",
                }}
                onClick={handleDownloadReceipt}
              >
                <i className="fa-regular fa-download me-2" />
                View receipt
              </button>
              <button
                type="button"
                className="btn flex-fill font-onest fw-semibold rounded-3"
                style={{
                  background: "#f3f4f6",
                  color: "#374151",
                  border: "1px solid #e5e7eb",
                  fontSize: "14px",
                  padding: "10px 16px",
                }}
                onClick={() => {
                  const text = `My rent reference: ${transaction?.referenceId || "—"} | Property: ${listing?.title || ""}`;
                  if (navigator.share) {
                    navigator.share({ title: "Rent info", text });
                  } else {
                    navigator.clipboard.writeText(text);
                    toast.success("Rent info copied to clipboard!");
                  }
                }}
              >
                <i className="fa-regular fa-share-nodes me-2" />
                Share rent info
              </button>
            </div>
          </div>

          {/* Next steps */}
          <div
            className="rounded-4 p-4 mb-5"
            style={{ background: "#f0faf8", border: "1px solid #d1f0ea" }}
          >
            <p
              className="fw-bold font-onest mb-3"
              style={{ fontSize: "14px", color: "#1a1a1a" }}
            >
              Next steps
            </p>
            {[
              {
                num: 1,
                title: "Check your email",
                desc: "We've sent your confirmation and rent details to your email address.",
              },
              {
                num: 2,
                title: "Complete identity verification (KYC)",
                desc: "Before you move in, you'll need to verify your identity. This takes under 5 minutes.",
                link: {
                  label: "Complete verification now →",
                  href: "/settings?tab=kyc",
                },
              },
              {
                num: 3,
                title: "Contact your landlord",
                desc: landlordFullName
                  ? `Feel free to message ${landlordFullName.split(" ")[0]} for any questions about your rent details.`
                  : "Feel free to contact your landlord for any questions.",
              },
            ].map((step) => (
              <div
                key={step.num}
                className="d-flex gap-3 mb-3 align-items-start"
              >
                <div
                  className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0 fw-bold font-onest"
                  style={{
                    width: "26px",
                    height: "26px",
                    background: "#47BBA9",
                    color: "#fff",
                    fontSize: "13px",
                  }}
                >
                  {step.num}
                </div>
                <div>
                  <p
                    className="fw-semibold font-onest mb-0"
                    style={{ fontSize: "14px", color: "#1a1a1a" }}
                  >
                    {step.title}
                  </p>
                  <p
                    className="font-onest mb-0"
                    style={{ fontSize: "13px", color: "#6b7280" }}
                  >
                    {step.desc}
                  </p>
                  {step.link && (
                    <a
                      href={step.link.href}
                      className="font-onest"
                      style={{ fontSize: "13px", color: "#47BBA9" }}
                    >
                      {step.link.label}
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Bottom actions */}
          <div className="d-flex flex-column flex-sm-row gap-3 align-items-center">
            <button
              type="button"
              className="btn w-100 fw-semibold font-onest rounded-4 py-3"
              style={{
                background: "#47BBA9",
                color: "#fff",
                border: "none",
                fontSize: "15px",
              }}
              onClick={() => navigate("/")}
            >
              Back to home
            </button>
            <button
              type="button"
              className="btn fw-semibold font-onest text-nowrap"
              style={{
                background: "transparent",
                color: "#374151",
                border: "none",
                fontSize: "14px",
              }}
              onClick={() =>
                navigate(isLongLease ? "/my-bookings?tab=rent" : "/my-bookings")
              }
            >
              View my bookings
            </button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  // ── Long-lease detail view ───────────────────────────────────────────────
  if (isLongLease) {
    const formatNairaVal = (v: number) =>
      `₦${Number(v || 0).toLocaleString("en-NG")}`;

    const statusBadge = (() => {
      const s = transaction?.status;
      if (s === "confirmed" || s === "customer-accepted")
        return { label: "Active", bg: "#d1fae5", color: "#065f46" };
      if (s === "completed")
        return { label: "Ended", bg: "#fee2e2", color: "#991b1b" };
      if (s === "cancelled")
        return { label: "Cancelled", bg: "#fee2e2", color: "#991b1b" };
      if (s === "pending_payment")
        return { label: "Pending Payment", bg: "#dbeafe", color: "#1e40af" };
      return {
        label: formatStatusLabel(s) || s,
        bg: "#f3f4f6",
        color: "#374151",
      };
    })();

    const tenantInitials =
      `${transaction?.renter?.firstname?.[0] || ""}${transaction?.renter?.lastname?.[0] || ""}`.toUpperCase() ||
      "?";
    const landlordName =
      `${transaction?.owner?.firstname || ""} ${transaction?.owner?.lastname || ""}`.trim();
    const landlordInitials =
      `${transaction?.owner?.firstname?.[0] || ""}${transaction?.owner?.lastname?.[0] || ""}`.toUpperCase() ||
      "L";

    const fmtDate = (d: string) =>
      d
        ? new Date(d).toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
          })
        : "—";

    return (
      <DashboardLayout
        title="Booking details"
        subtitle="View your booking details"
      >
        <div className={styles["booking-wrapper"]}>
          {/* Top Nav */}
          <div
            className={`${styles["top-nav"]} d-flex flex-column d-sm-flex flex-sm-row px-0`}
          >
            <button
              className={styles["back-btn"]}
              onClick={() =>
                navigate(isLongLease ? "/my-bookings?tab=rent" : "/my-bookings")
              }
            >
              <svg
                width="18"
                height="18"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M15 19l-7-7 7-7"
                />
              </svg>
              Back to my bookings
            </button>

            <div className={`${styles["nav-actions"]} d-flex gap-2`}>
              {verificationMissing && <Link
                className={styles["btn-book-again"]}
                to={`/settings?tab=tenant-verification&transaction=${encodeURIComponent(transaction?.id || transaction?._id || id || "")}`}
              >
                Tenant Verification
              </Link>}
              {(transaction?.status === "confirmed" ||
                transaction?.status === "customer-accepted" ||
                transaction?.status === "completed") && (
                <button
                  className={styles["btn-download"]}
                  onClick={handleDownloadReceipt}
                  >
                  <i className="fa-regular fa-download text-white"></i>
                  Download receipt
                </button>
              )}
              <button
                disabled={!canRenewRent(transaction)}
                title={canRenewRent(transaction) ? "Renew Rent" : renewalUnavailableMessage(transaction)}
                className={styles["btn-book-again"]}
                onClick={() => {
                  if (canRenewRent(transaction)) navigate(`/my-bookings/${transaction?.id}/renew`);
                }}
              >
                <svg
                  width="16"
                  height="16"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth="2.2"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M4 4v6h6M20 20v-6h-6M20 10A8 8 0 015.07 14.93M4 14A8 8 0 0118.93 9.07"
                  />
                </svg>
                Renew Rent
              </button>
            </div>
          </div>

          <div className={styles["main-content"]}>
            {/* ── Property header card ── */}
            <div className={styles["card"]}>
              <div
                className={`${styles["property-header"]} flex-column flex-md-row`}
              >
                {propertyData.imageUrl && !propertyImageFailed ? (
                  <img
                    src={propertyData.imageUrl}
                    alt={propertyData.name}
                    className={styles["property-img"]}
                    onError={() => setPropertyImageFailed(true)}
                  />
                ) : (
                  <div
                    className={styles["property-img-placeholder"]}
                    aria-label="No property image"
                  >
                    <i className="fa-regular fa-image" />
                  </div>
                )}

                <div className={styles["property-meta"]}>
                  {propertySlug ? (
                    <Link
                      to={`/property/${propertySlug}`}
                      className={`${styles["property-name"]} ${styles["property-name-link"]}`}
                    >
                      {propertyData.name}
                    </Link>
                  ) : (
                    <h2 className={styles["property-name"]}>
                      {propertyData.name}
                    </h2>
                  )}

                  <div className={`${styles["property-tags"]} flex-wrap`}>
                    {propertyData.rating > 0 && (
                      <span className={styles["star-rating"]}>
                        <i className="fa-solid fa-star text-warning"></i>
                        {propertyData.rating}
                        <span className={styles["review-count"]}>
                          ({propertyData.reviewCount} reviews)
                        </span>
                      </span>
                    )}
                    <span className={styles["location-tag"]}>
                      <svg
                        width="13"
                        height="13"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth="2.2"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M12 2C8.134 2 5 5.134 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.866-3.134-7-7-7z"
                        />
                        <circle cx="12" cy="9" r="2.5" />
                      </svg>
                      {[listing?.location?.city, listing?.location?.state]
                        .filter(Boolean)
                        .join(", ") || "—"}
                    </span>
                    <span className={styles["ref-tag"]}>
                      <svg
                        width="13"
                        height="13"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth="2.2"
                      >
                        <rect x="4" y="4" width="16" height="16" rx="2" />
                        <path d="M8 9h8M8 13h5" strokeLinecap="round" />
                      </svg>
                      Ref: {transaction?.referenceId || "—"}
                    </span>
                  </div>

                  <span
                    className={styles["badge-booked"]}
                    style={{
                      backgroundColor: statusBadge.bg,
                      color: statusBadge.color,
                    }}
                  >
                    {statusBadge.label}
                  </span>

                  {transaction?.status === "pending_payment" && (
                    <div className="d-flex flex-wrap gap-2 mt-3">
                      <button
                        className={styles["btn-download"]}
                        onClick={() => setShowPaymentModal(true)}
                      >
                        <i className="fa-regular fa-credit-card text-white"></i>
                        &nbsp;Pay Now
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Property details row */}
              <div style={{ borderTop: "1px solid #f0f0f5" }}>
                <h3 className={`${styles["section-title"]} font-onest`}>
                  Property details
                </h3>
                <div className={styles["details-grid"]}>
                  {[
                    {
                      icon: "fa-bed",
                      label: `${propertyData.bedrooms} Bedrooms`,
                    },
                    {
                      icon: "fa-bath",
                      label: `${propertyData.bathrooms} Bathrooms`,
                    },
                    ...(listing?.squareFoot
                      ? [
                          {
                            icon: "fa-ruler-combined",
                            label: `${listing.squareFoot} Squarefoot`,
                          },
                        ]
                      : []),
                  ].map((item) => (
                    <div className={styles["detail-item"]} key={item.label}>
                      <i
                        className={`fa-solid ${item.icon} ${styles["detail-icon"]}`}
                      ></i>
                      {item.label}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {rentStatusMessages[transaction?.status] && <div className="alert alert-info" role="status">{rentStatusMessages[transaction.status]}</div>}
            {["confirmed", "completed"].includes(transaction?.status) && <TenancyAgreementCard booking={transaction} />}
            {/* ── Rent details card ── */}
            <div className={styles["card"]}>
              <h3 className={`${styles["section-title"]} font-onest mb-3`}>
                Rent details
              </h3>
              <div className={styles["stay-grid"]}>
                {[
                  {
                    label: "Rent Date",
                    value: fmtDate(transaction?.commencementDate || transaction?.arrival_date),
                    icon: "fa-calendar",
                  },
                  {
                    label: "Rent End Date",
                    value: fmtDate(transaction?.departure_date),
                    icon: "fa-calendar",
                  },
                  { label: "Duration", value: rentDurationLabel || "—", icon: "fa-calendar" },
                  { label: "Caution fee status", value: ({ pending: "Held, refundable when your tenancy ends", requested: "Refund requested", "paid to renter": "Refunded", disputed: "Under review, our team will contact you" } as Record<string, string>)[transaction?.cautionFeeStatus] || formatStatusLabel(transaction?.cautionFeeStatus) || "—", icon: "fa-wallet" },
                  {
                    label: "Rent Amount",
                    value: formatNairaVal(
                      bill?.rentPerYear || bill?.propertyPrice,
                    ),
                    icon: "fa-calendar",
                  },
                  {
                    label: "Caution Fee (Refundable)",
                    value: formatNairaVal(bill?.cautionFee),
                    icon: "fa-calendar",
                  },
                ].map((box) => (
                  <div className={styles["stay-box"]} key={box.label}>
                    <div className={styles["stay-box-label"]}>
                      <i className={`fa-regular ${box.icon}`}></i>
                      {box.label}
                    </div>
                    <div className={styles["stay-box-value"]}>{box.value}</div>
                  </div>
                ))}
              </div>
              {showCautionFeeRefund && (
                <CautionFeeRefundButton transactionId={transaction.id} />
              )}
            </div>

            {/* ── Tenant + Landlord two-col ── */}
            <div className={styles["two-col"]}>
              {/* Tenant */}
              <div className={styles["card"]}>
                <p className={styles["section-label"]}>Tenant</p>
                <div className={styles["person-row"]}>
                  <div className={styles["avatar-circle"]}>
                    {tenantInitials}
                  </div>
                  <span className={styles["person-name"]}>
                    {`${transaction?.renter?.firstname || ""} ${transaction?.renter?.lastname || ""}`.trim()}
                  </span>
                </div>
                <div className={styles["contact-list"]}>
                  {transaction?.renter?.email && (
                    <div className={styles["contact-item"]}>
                      <i
                        className={`fa-regular fa-envelope ${styles["contact-icon"]}`}
                      ></i>
                      {transaction.renter.email}
                    </div>
                  )}
                  <div className="d-flex gap-3 flex-wrap">
                    {transaction?.renter?.phone && (
                      <div className={styles["contact-item"]}>
                        <i
                          className={`fa-solid fa-phone ${styles["contact-icon"]}`}
                        ></i>
                        {transaction.renter.phone}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Landlord */}
              <div className={styles["card"]}>
                <p className={styles["section-label"]}>Landlord</p>
                <div className={styles["person-row"]}>
                  {transaction?.owner?.photoUrl &&
                  transaction.owner.photoUrl !== "default.jpg" &&
                  !hostImageFailed ? (
                    <img
                      src={transaction.owner.photoUrl}
                      alt={landlordName}
                      className={styles["host-avatar"]}
                      onError={() => setHostImageFailed(true)}
                    />
                  ) : (
                    <div className={styles["host-avatar-placeholder"]}>
                      {landlordInitials}
                    </div>
                  )}
                  <div>
                    <div className={styles["person-name"]}>
                      {landlordName || "—"}
                    </div>
                    <div
                      style={{ fontSize: "13px", color: "#888", marginTop: 2 }}
                    >
                      Member since{" "}
                      {transaction?.owner?.createdAt
                        ? new Date(
                            transaction.owner.createdAt,
                          ).toLocaleDateString("en-US", {
                            month: "short",
                            year: "numeric",
                          })
                        : "2024"}
                    </div>
                  </div>
                  {transaction?.owner?.kycVerified && (
                    <span className={styles["verified-badge"]}>
                      <svg
                        width="12"
                        height="12"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth="2.2"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                      Verified
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        <PaymentMethodModal
          show={showPaymentModal}
          onClose={() => setShowPaymentModal(false)}
          bookingId={transaction?.id || transaction?._id || ""}
          totalAmount={bill?.total || 0}
          currency="₦"
          referenceId={transaction?.referenceId || ""}
        />
      </DashboardLayout>
    );
  }

  // ── Shortlet / Sale detail view (existing) ───────────────────────────────
  return (
    <DashboardLayout>
      <>
        <div className={styles["booking-wrapper"]}>
          {/* Top Nav */}
          <div
            className={`${styles["top-nav"]} d-flex flex-column d-sm-flex flex-sm-row px-0`}
          >
            <button
              className={styles["back-btn"]}
              onClick={() =>
                navigate(isLongLease ? "/my-bookings?tab=rent" : "/my-bookings")
              }
            >
              <svg
                width="18"
                height="18"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M15 19l-7-7 7-7"
                />
              </svg>
              Back to my bookings
            </button>
            <div className={`${styles["nav-actions"]} d-flex gap-2 gap-sm-3`}>
              <button
                className={`${styles["btn-download"]} w-100 w-sm-auto text-nowrap`}
                onClick={handleDownloadReceipt}
              >
                <i className="fa-regular fa-download text-white"></i>

                Download receipt
              </button>
              <button
                className={`${styles["btn-book-again"]} w-100 w-sm-auto`}
                onClick={() => {
                  if (propertySlug) {
                    navigate(`/property/${propertySlug}`);
                  }
                }}
              >
                <svg
                  width="16"
                  height="16"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth="2.2"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M4 4v6h6M20 20v-6h-6M20 10A8 8 0 015.07 14.93M4 14A8 8 0 0118.93 9.07"
                  />
                </svg>
                Book again
              </button>
            </div>
          </div>

          <div className={styles["main-content"]}>
            <div className={styles["card"]}>
              <div
                className={`${styles["property-header"]} flex-column flex-md-row`}
              >
                {propertyData.imageUrl && !propertyImageFailed ? (
                  <img
                    src={propertyData.imageUrl}
                    alt={propertyData.name}
                    className={styles["property-img"]}
                    onError={() => setPropertyImageFailed(true)}
                  />
                ) : (
                  <div
                    className={styles["property-img-placeholder"]}
                    aria-label="No property image"
                  >
                    <i className="fa-regular fa-image" />
                  </div>
                )}
                <div className={styles["property-meta"]}>
                  {propertySlug ? (
                    <Link
                      to={`/property/${propertySlug}`}
                      className={`${styles["property-name"]} ${styles["property-name-link"]}`}
                    >
                      {propertyData.name}
                    </Link>
                  ) : (
                    <h2 className={styles["property-name"]}>
                      {propertyData.name}
                    </h2>
                  )}
                  <div className={`${styles["property-tags"]} flex-wrap`}>
                    <span className={styles["star-rating"]}>
                      <i className="fa-solid fa-star text-warning"></i>
                      {propertyData.rating}
                      <span className={styles["review-count"]}>
                        ({propertyData.reviewCount} reviews)
                      </span>
                    </span>
                    <span className={styles["location-tag"]}>
                      <svg
                        width="13"
                        height="13"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth="2.2"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M12 2C8.134 2 5 5.134 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.866-3.134-7-7-7z"
                        />
                        <circle cx="12" cy="9" r="2.5" />
                      </svg>
                      {propertyData.location}
                    </span>
                    {propertySlug ? (
                      <Link
                        to={`/property/${propertySlug}`}
                        className={styles["ref-tag"]}
                      >
                        <svg
                          width="13"
                          height="13"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth="2.2"
                        >
                          <rect x="4" y="4" width="16" height="16" rx="2" />
                          <path d="M8 9h8M8 13h5" strokeLinecap="round" />
                        </svg>
                        Ref: {propertyData.ref}
                      </Link>
                    ) : (
                      <span className={styles["ref-tag"]}>
                        <svg
                          width="13"
                          height="13"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth="2.2"
                        >
                          <rect x="4" y="4" width="16" height="16" rx="2" />
                          <path d="M8 9h8M8 13h5" strokeLinecap="round" />
                        </svg>
                        Ref: {propertyData.ref}
                      </span>
                    )}
                  </div>
                  <span
                    className={`${styles["badge-booked"]} capitalize`}
                    style={{
                      backgroundColor:
                        propertyData.status === "confirmed" ||
                        propertyData.status === "customer-accepted" ||
                        propertyData.status === "completed"
                          ? "#d1fae5"
                          : propertyData.status === "cancelled"
                            ? "#fee2e2"
                            : propertyData.status === "progress" ||
                                propertyData.status === "awaiting-inspection"
                              ? "#dbeafe"
                              : propertyData.status === "pending_payment"
                                ? "#e0f2fe"
                                : propertyData.status ===
                                      "paid_pending_confirmation" ||
                                    propertyData.status ===
                                      "timeout_unconfirmed"
                                  ? "#fef3c7"
                                  : "#f3f4f6",
                      color:
                        propertyData.status === "confirmed" ||
                        propertyData.status === "customer-accepted" ||
                        propertyData.status === "completed"
                          ? "#065f46"
                          : propertyData.status === "cancelled"
                            ? "#991b1b"
                            : propertyData.status === "progress" ||
                                propertyData.status === "awaiting-inspection"
                              ? "#1e40af"
                              : propertyData.status === "pending_payment"
                                ? "#075985"
                                : propertyData.status ===
                                      "paid_pending_confirmation" ||
                                    propertyData.status ===
                                      "timeout_unconfirmed"
                                  ? "#92400e"
                                  : "#374151",
                    }}
                  >
                    {formatStatusLabel(propertyData.status)}
                  </span>
                  {propertyData.status === "pending_payment" && (
                    <div className="d-flex flex-wrap gap-2 mt-2">
                      <button
                        className={styles["btn-resume-payment"]}
                        onClick={handleResumePayment}
                        disabled={resumingPayment || cancelling}
                      >
                        {resumingPayment ? "Resuming..." : "Resume Payment"}
                      </button>
                      <button
                        className={styles["btn-cancel-transaction"]}
                        onClick={() => setShowCancelModal(true)}
                        disabled={resumingPayment || cancelling}
                      >
                        Cancel Transaction
                      </button>
                    </div>
                  )}
                </div>
                <div
                  className={`${styles["property-header-right"]} rounded-4`}
                  style={{ backgroundColor: "#47BBA90D" }}
                >
                  <span className="badge-non-instant font-onest d-flex gap-2 py-2 px-3">
                    <div className="bg-primary px-1 rounded-full">
                      <i className="fa-house fa-regular text-white " />
                    </div>
                    {propertyData.isInstant ? "Instant" : "Non Instant"}{" "}
                    Property
                  </span>
                </div>
              </div>

              {isReceiptUnderReview && (
                <div className={styles["host-wait-card"]}>
                  <div className={styles["host-wait-card__left"]}>
                    <div className={styles["host-wait-card__icon"]}>
                      <ClockIcon />
                    </div>
                    <div className={styles["host-wait-card__copy"]}>
                      <p className={styles["host-wait-card__eyebrow"]}>
                        Payment under review
                      </p>
                      <h4 className={styles["host-wait-card__title"]}>
                        {transaction?.hostAcceptedBankTransfer
                          ? "Your host accepted — Krent is confirming your transfer"
                          : "Receipt uploaded — Krent is verifying your transfer"}
                      </h4>
                      <p className={styles["host-wait-card__text"]}>
                        We’ll update this booking as soon as your payment is
                        confirmed.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {showHostWaitTimer && (
                <div className={styles["host-wait-card"]}>
                  <div className={styles["host-wait-card__left"]}>
                    <div className={styles["host-wait-card__icon"]}>
                      <ClockIcon />
                    </div>
                    <div className={styles["host-wait-card__copy"]}>
                      <p className={styles["host-wait-card__eyebrow"]}>
                        Awaiting host response
                      </p>
                      <h4 className={styles["host-wait-card__title"]}>
                        Your host has limited time to accept this booking
                      </h4>
                      <p className={styles["host-wait-card__text"]}>
                        We’ll update this booking as soon as they respond.
                        Please keep this page handy or check back shortly.
                      </p>
                    </div>
                  </div>
                  <div className={styles["host-wait-card__timer"]}>
                    <span className={styles["host-wait-card__timer-label"]}>
                      Time left
                    </span>
                    <span
                      className={styles["host-wait-card__timer-value"]}
                      aria-live="polite"
                    >
                      {hostWaitTime}
                    </span>
                  </div>
                </div>
              )}

              <div
                style={{
                  borderTop: "1px solid #f0f0f5",
                }}
              >
                <h3 className={`${styles["section-title"]} font-onest`}>
                  Property details
                </h3>
                <div
                  className={`${styles["details-grid"]} grid-cols-2 grid-cols-md-4`}
                >
                  {[
                    {
                      icon: "fa-users",
                      label: `${propertyData.guests} Guests`,
                    },
                    {
                      icon: "fa-bed",
                      label: `${propertyData.bedrooms} Bedrooms`,
                    },
                    {
                      icon: "fa-bath",
                      label: `${propertyData.bathrooms} Bathrooms`,
                    },
                  ].map((item) => (
                    <div className={styles["detail-item"]} key={item.label}>
                      <i
                        className={`fa-solid ${item.icon} ${styles["detail-icon"]}`}
                      ></i>
                      {item.label}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className={styles["card"]}>
              <h3 className={styles["section-title"]}>Stay Details</h3>
              <div className={styles["stay-grid"]}>
                {[
                  {
                    label: "Check-in",
                    value: stayData.checkIn,
                    icon: "fa-calendar fa-regular",
                  },
                  {
                    label: "Check-out",
                    value: stayData.checkOut,
                    icon: "fa-calendar fa-regular",
                  },
                  {
                    label: "Guests",
                    value: `${stayData.guests} guests`,
                    icon: "fa-users",
                  },
                  {
                    label: "Nights",
                    value: stayData.nights,
                    icon: "fa-moon",
                  },
                ].map((box) => (
                  <div className={styles["stay-box"]} key={box.label}>
                    <div className={styles["stay-box-label"]}>
                      <i className={`fa-solid ${box.icon}`}></i>
                      {box.label}
                    </div>
                    <div className={styles["stay-box-value"]}>{box.value}</div>
                  </div>
                ))}
              </div>

              <div className="border rounded-4 p-4">
                <div className={styles["pricing-row"]}>
                  <span>
                    {formatNaira(stayData.nightlyRate)} × {stayData.nights}{" "}
                    nights
                  </span>
                  <span>
                    {formatNaira(stayData.nightlyRate * stayData.nights)}
                  </span>
                </div>
                {stayData.serviceFee > 0 && (
                  <div className={styles["pricing-row"]}>
                    <span>Service fee</span>
                    <span>{formatNaira(stayData.serviceFee)}</span>
                  </div>
                )}

                <div className={styles["pricing-row"]}>
                  <span>Caution fee (refundable)</span>
                  <span>{formatNaira(stayData.cautionFee)}</span>
                </div>
                <div className={styles["pricing-row"] + " " + styles["total"]}>
                  <span>Total paid</span>
                  <span>{formatNaira(totalPaid)}</span>
                </div>
              </div>
              {showCautionFeeRefund && (
                <CautionFeeRefundButton transactionId={transaction.id} />
              )}
            </div>

            <div className={styles["two-col"]}>
              <div className={styles["card"]}>
                <p className={styles["section-label"]}>Guests</p>
                <div className={styles["person-row"]}>
                  <div className={styles["avatar-circle"]}>
                    {guestData.initials}
                  </div>
                  <span className={styles["person-name"]}>
                    {guestData.name}
                  </span>
                </div>
                {isOwnGuest &&
                  (guestData.email ||
                    guestData.phone ||
                    guestData.whatsapp) && (
                    <div className={styles["contact-list"]}>
                      {guestData.email && (
                        <div className={styles["contact-item"]}>
                          <i
                            className={`fa-solid fa-envelope ${styles["contact-icon"]}`}
                          ></i>
                          {guestData.email}
                        </div>
                      )}
                      {(guestData.phone || guestData.whatsapp) && (
                        <div style={{ display: "flex", gap: "16px" }}>
                          {guestData.phone && (
                            <div className={styles["contact-item"]}>
                              <i
                                className={`fa-solid fa-phone ${styles["contact-icon"]}`}
                              ></i>
                              {guestData.phone}
                            </div>
                          )}
                          {guestData.whatsapp && (
                            <div className={styles["contact-item"]}>
                              <i
                                className={`fa-solid fa-comment ${styles["contact-icon"]}`}
                              ></i>
                              {guestData.whatsapp}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                <div className={styles["requests-box"]}>
                  {specialRequests || "Your special requests"}
                </div>
              </div>

              <div className={styles["card"]}>
                <p className={styles["section-label"]}>Host</p>
                <div className={styles["person-row"]}>
                  {hostData.imageUrl && !hostImageFailed ? (
                    <img
                      src={hostData.imageUrl}
                      alt={hostData.name}
                      className={styles["host-avatar"]}
                      onError={() => setHostImageFailed(true)}
                    />
                  ) : (
                    <div
                      className={styles["host-avatar-placeholder"]}
                      aria-label="No host photo"
                    >
                      {hostData.name
                        ?.split(" ")
                        .map((n) => n[0])
                        .join("")
                        .slice(0, 2)
                        .toUpperCase() || "H"}
                    </div>
                  )}
                  <div>
                    <div className={styles["person-name"]}>{hostData.name}</div>
                    <div
                      style={{ fontSize: "13px", color: "#888", marginTop: 2 }}
                    >
                      Member since {hostData.memberSince}
                    </div>
                  </div>
                  {hostData.isVerified && (
                    <span className={styles["verified-badge"]}>
                      <svg
                        width="12"
                        height="12"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth="2.2"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                      Verified
                    </span>
                  )}
                </div>
                {/* <div className={styles["contact-list"]}>
                  <div className={styles["contact-item"]}>
                    <i className={`fa-solid fa-envelope ${styles["contact-icon"]}`}></i>
                    {hostData.email}
                  </div>
                  <div className={styles["contact-item"]}>
                    <i className={`fa-solid fa-phone ${styles["contact-icon"]}`}></i>
                    {hostData.phone}
                  </div>
                </div> */}
              </div>
            </div>
            {transaction.status === "confirmed" && (
              <div className={styles["card"]}>
                <RatingsAndReviews
                  propertyId={listing?.id || listing?._id || ""}
                  showPostReview={true}
                />
              </div>
            )}
          </div>
        </div>

        <Modal
          center
          open={showCancelModal}
          onClose={() => {
            if (!cancelling) setShowCancelModal(false);
          }}
          showCloseIcon={false}
          focusTrapped={false}
          animationDuration={200}
          classNames={{
            overlay: styles["cancel-overlay"],
            modal: styles["cancel-modal"],
          }}
          aria-labelledby="cancel-transaction-title"
          aria-describedby="cancel-transaction-description"
        >
          <div className={styles["cancel-content"]}>
            <div className={styles["cancel-icon"]}>
              <svg
                width="22"
                height="22"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
                />
              </svg>
            </div>
            <h6
              className={styles["cancel-title"]}
              id="cancel-transaction-title"
            >
              Cancel this booking?
            </h6>
            <p
              className={styles["cancel-description"]}
              id="cancel-transaction-description"
            >
              This will cancel your pending payment booking. You can always book
              again later.
            </p>
            <div className={styles["cancel-actions"]}>
              <button
                type="button"
                className={styles["cancel-keep-btn"]}
                onClick={() => setShowCancelModal(false)}
                disabled={cancelling}
              >
                Keep booking
              </button>
              <button
                type="button"
                className={styles["cancel-confirm-btn"]}
                onClick={handleCancelTransaction}
                disabled={cancelling}
              >
                {cancelling ? "Cancelling..." : "Yes, cancel"}
              </button>
            </div>
          </div>
        </Modal>
      </>
    </DashboardLayout>
  );
}
